import { Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type { InstrumentNumberFormat } from 'lib-analytics-types';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import isNumber from 'lodash/isNumber';
import { memo, useMemo, useState } from 'react';
import { usePrevious, useUpdateEffect } from 'react-use';

import {
  HeatmapInstrumentTooltip,
  HeatmapInstrumentTooltipProps,
} from '../tooltip/heatmapInstrumentTooltip';

import styles from './heatmapInstrumentCell.module.scss';

type TextProps = Partial<React.ComponentProps<typeof Text.Paragraph>>;

/**
 * Represents the unique ID of a cell, consisting of the row index and cell
 * index.
 */
export type HeatmapInstrumentCellId = [row: number, column: number];

/** Represents a cell in the heatmap. */
export type HeatmapInstrumentCell = {
  /** The unique ID of the cell, if any. */
  id: HeatmapInstrumentCellId;
  /** The value of the cell. */
  value: string | number | null;
  /** The formatting method used to display the value. */
  format?: InstrumentNumberFormat;
};

export type HeatmapInstrumentCellProps = {
  /** The cell. */
  cell?: HeatmapInstrumentCell;
  /** Whether the cell is dimmed. */
  dimmed?: boolean;
  /** The background color of the cell. */
  background?: string;
  /** Props passed to the Tooltip component. */
  tooltipProps?: {
    /** Whether the tooltip is shown. */
    enabled: boolean;
  } & HeatmapInstrumentTooltipProps;
  /** Props passed to the Text.Paragraph component. */
  textProps?: TextProps;
  /** Callback when hovering the cell. */
  onMouseEnter: React.MouseEventHandler<HTMLDivElement>;
};

export const HeatmapInstrumentCell = memo(function HeatmapInstrumentCell({
  cell,
  dimmed = false,
  background = 'transparent',
  tooltipProps,
  textProps = {},
  onMouseEnter,
}: HeatmapInstrumentCellProps): JSX.Element {
  const [animateChange, setAnimateChange] = useState(false);
  const prevValue = usePrevious(cell?.value);

  useUpdateEffect(() => {
    if (cell?.value !== prevValue) {
      setAnimateChange(true);

      const timeoutID = setTimeout(() => {
        setAnimateChange(false);
      }, 200);

      return () => {
        clearTimeout(timeoutID);
      };
    }
  }, [cell?.value]);

  const formattedValue = useMemo(() => {
    if (!cell) return null;

    const { value, format } = cell;

    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' && format) {
      return formatInstrumentNumber(value, format);
    }
    return '—';
  }, [cell]);

  if (!cell) {
    return (
      <div
        className={styles.cell}
        onMouseEnter={onMouseEnter}
        data-testid="heatmap-instrument-cell"
      />
    );
  }

  const cellContent: JSX.Element = (
    <div className={styles.cellContent}>
      <Text.Paragraph
        className={classNames({
          [styles.cellText]: typeof cell.value === 'string',
        })}
        variant="3"
        subdued={!isNumber(cell.value)}
        {...textProps}
      >
        {formattedValue}
      </Text.Paragraph>
    </div>
  );

  return (
    <div
      className={classNames(styles.cell, {
        [styles.cellDimmed]: dimmed || animateChange,
      })}
      onMouseEnter={onMouseEnter}
      style={{ background }}
      data-animating={animateChange}
      data-testid="heatmap-instrument-cell"
    >
      {tooltipProps?.enabled ? (
        <HeatmapInstrumentTooltip {...tooltipProps}>
          {cellContent}
        </HeatmapInstrumentTooltip>
      ) : (
        cellContent
      )}
    </div>
  );
});
